import React, { useState, useEffect, useRef } from "react";
import { getOneStaff } from "../api/staff-calls-api";
import { 
  Mail, Phone, MapPin, Briefcase, Calendar, User, DollarSign, Banknote, 
  School, Heart, X, Info, UserCircle 
} from "lucide-react";

/**
 * A reusable component to display a detail item with an icon.
 * @param {React.ReactNode} icon - The icon component from lucide-react.
 * @param {string} label - The label for the detail.
 * @param {string|number} value - The value of the detail.
 * @param {boolean} fullWidth - Optional prop to make the item take full width on smaller screens.
 */
const DetailItem = ({ icon, label, value, fullWidth = false }) => (
    <div className={`flex items-start py-3 px-4 bg-white rounded-lg border border-gray-200 shadow-sm ${fullWidth ? 'col-span-1 md:col-span-2' : ''}`}>
        <div className="text-orange-500 mr-4 mt-1 flex-shrink-0">{icon}</div>
        <div className="min-w-0">
            <p className="text-sm text-gray-500">{label}</p>
            <p className="font-semibold text-gray-800 truncate">{value || "N/A"}</p>
        </div>
    </div>
);

/**
 * A modal to display comprehensive, read-only details of a staff member.
 * @param {string} staffId - The ID of the staff member to view.
 * @param {Function} closeModal - Function to close the modal.
 */
export default function ViewStaffModal({ staffId, closeModal }) {
    const modalRef = useRef(null);
    const [staffData, setStaffData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Click outside to close modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [closeModal]);

    useEffect(() => {
        async function fetchStaff() {
            try {
                const res = await getOneStaff(staffId);
                setStaffData(res.data);
            } catch (err) {
                setError("Failed to load staff info");
            } finally {
                setLoading(false);
            }
        }
        fetchStaff();
    }, [staffId]);

    // Format date string to a more readable format
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
                    <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-lg font-medium text-gray-700">Loading staff information...</p>
                </div>
            </div>
        );
    }

    if (error || !staffData) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                            <X className="h-8 w-8 text-red-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Staff Data</h3>
                        <p className="text-gray-500 mb-6">{error || "Staff data not available"}</p>
                        <button
                            onClick={closeModal}
                            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg hover:shadow-xl"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // A helper function to create a detail item with a label and value
    const detail = (label, value, icon, fullWidth = false) => (
        <DetailItem icon={icon} label={label} value={value} fullWidth={fullWidth} />
    );

    // Destructure nested objects for easier access
    const {
        residence = {},
        nextOfKin = {},
        bankingDetails = [],
        qualifications = [],
        workHistory = {}
    } = staffData;

    // Use the first item from the bankingDetails and qualifications arrays
    const bank = bankingDetails[0] || {};
    const education = qualifications[0] || {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
            >
                {/* Header with Orange Gradient */}
                <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
                    <div className="flex items-center justify-between px-8 py-6">
                        <div className="flex items-center space-x-4">
                            <div className="p-3 bg-black/20 backdrop-blur-sm rounded-xl shadow-sm">
                                <UserCircle className="w-7 h-7 text-black" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-black">Staff Profile</h2>
                                <p className="text-black-100 mt-1">
                                    {staffData.firstName} {staffData.lastName}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={closeModal}
                            className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-grow px-8 py-6 overflow-y-auto">
                    <div className="space-y-8">
                        {/* Personal Information */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <User className="text-orange-500" /> Personal Information
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {detail("Full Name", `${staffData.firstName} ${staffData.lastName}`, <User size={20} />)}
                                {detail("Email", staffData.email, <Mail size={20} />)}
                                {detail("Mobile", staffData.mobile, <Phone size={20} />)}
                                {detail("Gender", staffData.gender, <User size={20} />)}
                                {detail("Date of Birth", formatDate(staffData.dateOfBirth), <Calendar size={20} />)}
                                {detail("Marital Status", staffData.maritalStatus, <Heart size={20} />)}
                                {detail("Nationality", staffData.nationality, <MapPin size={20} />)}
                                {detail("National ID / Passport", staffData.nationalIdOrPassport, <Briefcase size={20} />)}
                                {detail("Village / Block", residence.villageOrBlock, <MapPin size={20} />)}
                                {detail("Payam", residence.payam, <MapPin size={20} />)}
                                {detail("County", residence.county, <MapPin size={20} />)}
                                {detail("State", residence.state, <MapPin size={20} />)}
                            </div>
                        </div>

                        {/* Employment Details */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <Briefcase className="text-orange-500" /> Employment Details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {detail("Job Title", staffData.jobTitle, <Briefcase size={20} />)}
                                {detail("Department", staffData.department, <Briefcase size={20} />)}
                                {detail("Role", staffData.role, <Briefcase size={20} />)}
                                {detail("Date of Employment", formatDate(staffData.dateOfEmployment), <Calendar size={20} />)}
                                {detail("Employment Type", staffData.employmentType, <Briefcase size={20} />)}
                                {detail("Contract Duration", staffData.contractDuration, <Calendar size={20} />)}
                                {detail("Salary Range", staffData.salaryRange, <DollarSign size={20} />)}
                            </div>
                        </div>

                        {/* Bank Details */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <Banknote className="text-orange-500" /> Bank Details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {detail("Bank Name", bank.bankName, <Banknote size={20} />)}
                                {detail("Account Name", bank.accountName, <Banknote size={20} />)}
                                {detail("Account Number", bank.accountNumber, <Banknote size={20} />)}
                                {detail("Currency", bank.currency, <DollarSign size={20} />)}
                                {detail("SWIFT Code", bank.swiftCode, <Banknote size={20} />)}
                                {detail("Bank Branch", bank.bankBranch, <Banknote size={20} />)}
                            </div>
                        </div>

                        {/* Education & Work History */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <School className="text-orange-500" /> Education & Work History
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {detail("Institution", education.institution, <School size={20} />)}
                                {detail("Award", education.award, <School size={20} />)}
                                {detail("Year From", education.yearFrom, <Calendar size={20} />)}
                                {detail("Year To", education.yearTo, <Calendar size={20} />)}
                                {detail("Key Expertise", staffData.keyExpertise, <Briefcase size={20} />)}
                                {detail("Recent Employer", workHistory.recentEmployer, <Briefcase size={20} />)}
                                {detail("Role", workHistory.workrole, <Briefcase size={20} />)}
                                {detail("Period of Service", workHistory.periodOfService, <Calendar size={20} />)}
                                {detail("Recent Gross Annual Payment", workHistory.recentGrossAnnualPaymentRange, <DollarSign size={20} />)}
                            </div>
                        </div>

                        {/* Next of Kin */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <Heart className="text-orange-500" /> Next of Kin
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {detail("Name", nextOfKin.name, <User size={20} />)}
                                {detail("Relationship", nextOfKin.relationship, <User size={20} />)}
                                {detail("Nationality", nextOfKin.nationality, <MapPin size={20} />)}
                                {detail("Village or Block", nextOfKin.residence?.villageOrBlock, <MapPin size={20} />)}
                                {detail("Payam", nextOfKin.residence?.payam, <MapPin size={20} />)}
                                {detail("County", nextOfKin.residence?.county, <MapPin size={20} />)}
                                {detail("State", nextOfKin.residence?.state, <MapPin size={20} />)}
                                {detail("Telephone", nextOfKin.tel, <Phone size={20} />)}
                                {detail("Mobile", nextOfKin.mobile, <Phone size={20} />)}
                                {detail("Email", nextOfKin.email, <Mail size={20} />)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Info className="w-4 h-4 text-orange-500" />
                                <span>Staff profile information</span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <button
                                onClick={closeModal}
                                className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}